import React from "react";
import { Typography } from "@mui/material";
import { useBusinessTheme } from "../../hooks/useBusinessTheme";
import CommonButtons from "../CommonButtons";
import { ModalWrapper } from "../ModalWrapper";
import InputGroup from "src/business-types/grocery/layouts/setting/customer/InputGroup";

export interface FormFieldConfig {
  id: string;
  label?: string;
  required?: boolean;
  type?: "text" | "email" | "phone" | "number" | "section" | "custom";
  value?: string;
  onChange?: (val: string) => void;
  error?: boolean;
  helperText?: string;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  gridSpan?: 1 | 2;
  render?: React.ReactNode;
}

interface GenericFormProps {
  open: boolean;
  title: string;
  fields: FormFieldConfig[];
  onClose: () => void;
  onSubmit: () => void;
  isFormValid: () => boolean;
  confirmLabel?: string;
  cancelLabel?: string;
  children?: React.ReactNode;
  width?: string;
  showBackButton?: boolean;
  onBack?: () => void;
  isLoading?: boolean;
}

const GenericForm: React.FC<GenericFormProps> = ({
  open,
  title,
  fields,
  onClose,
  onSubmit,
  isFormValid,
  confirmLabel = "Submit",
  cancelLabel = "Cancel",
  children,
  width = "max-w-xl",
  showBackButton = false,
  onBack,
  isLoading = false,
}) => {
  useBusinessTheme();

  if (!open) return null;

  return (
    <ModalWrapper title={title} onClose={onClose} showCloseButton showBackButton={showBackButton} onBack={onBack} width={width} disableClose={isLoading}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 border border-gray-300 rounded-xl p-3 bg-gray-50 shadow-inner space-y-1">
        {fields.map((field) => {
          const spanClass = field.type === "section" || field.gridSpan === 2 ? "md:col-span-2" : "";

          if (field.type === "section") {
            return (
              <div key={field.id} className={`mt-2 ${spanClass}`}>
                <Typography variant="subtitle1" fontWeight="bold">
                  {field.label}
                </Typography>
              </div>
            );
          }
          /* -------- Custom (ImageUploader etc.) -------- */
          if (field.type === "custom") {
            return (
              <div key={field.id} className={spanClass}>
                {field.render}
              </div>
            );
          }

          return (
            <div key={field.id} className={spanClass}>
              <InputGroup
                label={field.label || ""}
                type={field.type || "text"}
                name={field.id}
                value={field.value || ""}
                mandatory={field.required}
                disabled={field.disabled || isLoading}
                onKeyDown={field.onKeyDown}
                onChange={(_, value) => field.onChange?.(value)}
              />

              {field.error && field.helperText && <p className="text-xs text-red-600 mt-1">{field.helperText}</p>}
            </div>
          );
        })}
      </div>

      {children}

      <div className="flex justify-end gap-3 mt-6">
        <CommonButtons className="w-36 h-10" label={cancelLabel} variant="outlineDanger" onClick={onClose} disabled={isLoading} />

        <CommonButtons className="w-36 h-10" label={confirmLabel} onClick={onSubmit} disabled={!isFormValid() || isLoading} loading={isLoading} />
      </div>
    </ModalWrapper>
  );
};

export default GenericForm;
